import React from 'react';
import { motion } from 'motion/react';
import { Info, Trophy, Play, Check, ArrowRight, TrendingUp, ClipboardList, Share, Sparkles, Shield, Repeat, Users } from 'lucide-react';
import { GS } from '../lib/designTokens';
import { SplitFlapWord } from './SplitFlapWord';
import { track } from '../lib/analytics';

// Sports/Pro marketing landing page for logged-out coaches. All auth logic stays
// in the Login component (App.tsx) and is passed in as props — this file is the
// presentation layer only.
//
// DESIGN: "Green & Slate · Under the Lights" — near-white slate canvas, white
// cards with soft shadows, green as the everyday accent, gold reserved for Elite.
// Surface colors come from inline GS.* hex (NOT Tailwind color classes) because
// index.css's `.light` class inverts the zinc palette — see designTokens.ts.
interface LandingPageProps {
  onGoogleLogin: () => void;
  /** Sign in with Apple (works on web + native). Button renders when provided. */
  onAppleLogin?: () => void;
  onTryDemo: () => void;
  demoLoading: boolean;
  demoError: string;
  inAppBrowser: boolean;
  /** Optional demo video. MP4/webm URL → inline <video>; otherwise the poster
   *  section falls back to launching the live interactive demo. Supply later. */
  demoVideoUrl?: string;
  // Email auth (state + handlers owned by Login)
  emailMode: boolean; setEmailMode: (v: boolean) => void;
  authMode: 'signin' | 'signup'; setAuthMode: (m: 'signin' | 'signup') => void;
  signInOpen: boolean; setSignInOpen: (v: boolean) => void;
  /** Optional: lift the signup ("Create Account") overlay state to the parent so
   *  a native welcome screen can open it. Omit on web to keep it self-contained. */
  signupOpen?: boolean; setSignupOpen?: (v: boolean) => void;
  email: string; setEmail: (v: string) => void;
  password: string; setPassword: (v: string) => void;
  signupName: string; setSignupName: (v: string) => void;
  authError: string; setAuthError: (v: string) => void;
  authLoading: boolean;
  resetSent: boolean;
  showReset: boolean; setShowReset: (v: boolean) => void;
  onEmailSubmit: (e: React.FormEvent) => void;
  onPasswordReset: () => void;
  onShowTerms: () => void;
  onShowPrivacy: () => void;
}

// ── Reusable style atoms (inline so the .light zinc inversion can't touch them) ──
const suMark: React.CSSProperties = {
  width: 32, height: 32, borderRadius: 9,
  background: `linear-gradient(150deg, ${GS.green}, ${GS.green2})`,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  boxShadow: `0 4px 14px ${GS.greenGlow}`,
};
const eyebrow: React.CSSProperties = {
  fontSize: 11, fontWeight: 800, letterSpacing: '0.18em',
  textTransform: 'uppercase', color: GS.greenInk,
};
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '12px 16px', borderRadius: 12,
  border: `1px solid ${GS.line}`, background: GS.panel2,
  fontSize: 14, color: GS.ink, outline: 'none',
};
const APP_STORE_URL = 'https://apps.apple.com/tt/app/dugout-baseball-coach/id6781730520';

/**
 * September Push — 72 Fall Season Passes, free through 1 March 2027.
 *
 * Date-driven so the page reverts to the standard trial pitch on its own. A
 * hardcoded boolean would depend on someone remembering to flip it on 1 October,
 * and a page still advertising a closed campaign is worse than one that never
 * ran it.
 *
 * ⚠️ This instant MUST match `windowEnd` on `meta/campaignSeptemberPush`, which is
 * what grantSeasonPassOnSignup actually enforces server-side. If they drift, the
 * page promises a pass the server will decline. Both are 30 Sept 2026 23:59:59
 * Eastern.
 *
 * Deliberately does NOT read the live `claimed` count: a "3 of 72 left" counter
 * that barely moves advertises the wrong thing.
 */
const SEPTEMBER_PUSH = Date.now() <= Date.parse('2026-09-30T23:59:59-04:00');

/**
 * All 72 passes are spoken for — the campaign is closed early (2026-09-18).
 *
 * The campaign was never advertised, and the passes that went out came in
 * organically via Apple and the web. Rather than pull the page down, the cap on
 * `meta/campaignSeptemberPush` was lowered to the number already claimed, so the
 * server now declines every new signup with "campaign full". This flag is what
 * keeps the PAGE honest about that: the story stays up, but nothing still offers
 * a pass that cannot be granted.
 *
 * Deliberately a hardcoded boolean, not a read of the live `claimed` count. The
 * cap is set in the Firebase Console and this page is deployed; a counter that
 * flipped the page's whole pitch on a Firestore read would put the marketing
 * message at the mercy of a doc edit. Sold out is a decision, so it is stated.
 *
 * Both this and SEPTEMBER_PUSH stop mattering on 30 Sept, when the campaign
 * blocks disappear on their own.
 */
const PASSES_SOLD_OUT = true;
// Apple's official "Download on the App Store" badge artwork (public/app-store-badge.svg).
// Apple's marketing guidelines require the unaltered badge — size it only via height,
// and never rebuild the lockup by hand.
const AppStoreBadge = ({ height = 40, variant = 'black' }: { height?: number; variant?: 'black' | 'white' }) => (
  <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', lineHeight: 0 }}>
    <img
      src={variant === 'white' ? '/app-store-badge-white.svg' : '/app-store-badge.svg'}
      alt="Download on the App Store"
      style={{ height, width: 'auto', display: 'block' }}
    />
  </a>
);

// Section-level CTA. Every rung of the page ends with one of these, worded
// in the vocabulary of the headache that section just described.
//
// One step darker than GS.greenInk, for the CTA hover state.
const CTA_HOVER = '#05563A';

// Solid deep green (greenInk) with white text. Deliberately NOT the earlier
// treatment — a pale green ground inside a green border with green text is
// three values of one hue hedging at each other, and it reads as generated.
// One flat committed color is what a person would draw. It also stays
// distinct from the hero/price buttons, which use the BRIGHT green gradient
// (green → green2), so the visual hierarchy survives without the outline.
const SectionCta = ({ label, onClick, hint, align = 'left' }: { label: string; onClick: () => void; hint?: string; align?: 'left' | 'center' }) => (
  <div style={{ marginTop: 28, textAlign: align }}>
    <button
      onClick={onClick}
      onMouseEnter={e => { e.currentTarget.style.background = CTA_HOVER; }}
      onMouseLeave={e => { e.currentTarget.style.background = GS.greenInk; }}
      style={{
        background: GS.greenInk, border: 'none', cursor: 'pointer',
        color: '#FFFFFF', fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase',
        fontSize: 15, letterSpacing: '0.01em', borderRadius: 12, padding: '16px 28px',
        display: 'inline-flex', alignItems: 'center', gap: 9,
        boxShadow: '0 6px 20px -6px rgba(6,122,82,0.45)', transition: 'background 140ms',
      }}
    >
      {label} <ArrowRight style={{ width: 17, height: 17 }} />
    </button>
    {/* Lowers the perceived cost of the click right where it's asked for. */}
    {hint && (
      <p style={{ fontSize: 12, fontWeight: 700, color: GS.ink3, marginTop: 10 }}>{hint}</p>
    )}
  </div>
);

// Shared section head. Normalizes eyebrow → h2 → deck across every section
// so the page has one rhythm instead of the three competing ones it grew.
const SectionHead = ({ eyebrowText, children, deck, align = 'left', maxWidth = 460 }: { eyebrowText: string; children: React.ReactNode; deck?: React.ReactNode; align?: 'left' | 'center'; maxWidth?: number }) => (
  <div style={{ textAlign: align, marginBottom: align === 'center' ? 44 : 0 }}>
    <span style={{ ...eyebrow, display: 'inline-block', marginBottom: 12 }}>{eyebrowText}</span>
    <h2 style={{ fontSize: 'clamp(32px, 6vw, 44px)', letterSpacing: '-0.02em', fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase', lineHeight: 1.02 }}>
      {children}
    </h2>
    {deck && (
      <p style={{ color: GS.ink2, marginTop: 16, fontSize: 16, lineHeight: 1.55, maxWidth, ...(align === 'center' ? { marginLeft: 'auto', marginRight: 'auto' } : {}) }}>
        {deck}
      </p>
    )}
  </div>
);

// Season progression for the hero card. Real measured values from a 2026
// player: scout 86, eleven games, blended 95. Presentational only — this
// draws numbers, it does not compute them (xOVR is calculated client-side
// in src/lib/xovr-engine.ts and nothing here touches it).
const XOVR_SERIES = [88, 90, 89, 93, 93, 95, 95, 95, 97, 97, 95];
const SCOUT_BASE = 86;

const XovrTrendline = () => {
  // Fixed viewBox + preserveAspectRatio="none" on the fill only; the line
  // and dots keep their aspect so strokes stay even at any card width.
  const W = 440, H = 168, PAD_L = 34, PAD_R = 14, PAD_T = 12, PAD_B = 26;
  const MIN = 81, MAX = 100;                       // matches the in-app axis
  const x = (i: number) => PAD_L + (i * (W - PAD_L - PAD_R)) / (XOVR_SERIES.length - 1);
  const y = (v: number) => PAD_T + ((MAX - v) * (H - PAD_T - PAD_B)) / (MAX - MIN);
  const line = XOVR_SERIES.map((v, i) => `${i ? 'L' : 'M'}${x(i)},${y(v)}`).join(' ');

  return (
    <div style={{ padding: '0 20px 4px' }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }} role="img" aria-label="xOVR trendline rising from 86 to 95 across eleven games">
        <defs>
          <linearGradient id="dugoutTrendFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={GS.green} stopOpacity="0.20" />
            <stop offset="100%" stopColor={GS.green} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* gridlines + labels */}
        {[100, 96, 91].map(v => (
          <g key={v}>
            <line x1={PAD_L} y1={y(v)} x2={W - PAD_R} y2={y(v)} stroke={GS.line} strokeWidth="1" strokeDasharray="4 5" />
            <text x={PAD_L - 9} y={y(v) + 4} textAnchor="end" fontSize="10" fontWeight="800" fill={GS.ink3}>{v}</text>
          </g>
        ))}

        {/* the scout anchor — solid, flat, and labelled, so the rise is
            read against the coach's original read rather than against zero */}
        <line x1={PAD_L} y1={y(SCOUT_BASE)} x2={W - PAD_R} y2={y(SCOUT_BASE)} stroke={GS.ink3} strokeWidth="1" />
        <text x={PAD_L - 9} y={y(SCOUT_BASE) + 4} textAnchor="end" fontSize="10" fontWeight="800" fill={GS.ink3}>{SCOUT_BASE}</text>
        <text x={W - PAD_R} y={y(SCOUT_BASE) - 7} textAnchor="end" fontSize="9" fontWeight="700" fill={GS.ink3}>Scout {SCOUT_BASE}</text>

        <path d={`${line} L${x(XOVR_SERIES.length - 1)},${y(MIN)} L${x(0)},${y(MIN)} Z`} fill="url(#dugoutTrendFill)" />
        <motion.path
          d={line} fill="none" stroke={GS.green} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.1, delay: 0.35, ease: 'easeOut' }}
        />
        {XOVR_SERIES.map((v, i) => (
          <motion.circle
            key={i} cx={x(i)} cy={y(v)} r={i === XOVR_SERIES.length - 1 ? 4.5 : 3.2}
            fill={GS.green} stroke={GS.panel} strokeWidth="1.5"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 0.2, delay: 0.4 + i * 0.09 }}
          />
        ))}
        {XOVR_SERIES.map((_, i) => (
          <text key={i} x={x(i)} y={H - 6} textAnchor="middle" fontSize="9.5" fontWeight="800" fill={GS.ink3}>G{i + 1}</text>
        ))}
      </svg>
    </div>
  );
};

export const LandingPage = (props: LandingPageProps) => {
  const {
    onGoogleLogin, onAppleLogin, onTryDemo, demoLoading, demoError, inAppBrowser, demoVideoUrl,
    emailMode, setEmailMode, authMode, setAuthMode, email, setEmail,
    password, setPassword, signupName, setSignupName, authError, setAuthError, authLoading, resetSent,
    showReset, setShowReset, onEmailSubmit, onPasswordReset, onShowTerms, onShowPrivacy,
    signInOpen, setSignInOpen,
    signupOpen: signupOpenProp, setSignupOpen: setSignupOpenProp,
  } = props;

  // Signup overlay (Create Your Account) — Google + name/email/password, the
  // primary new-coach surface opened by the hero "Sign up free" button.
  // Lift-able: when the parent supplies signupOpen/setSignupOpen (native, so the
  // NativeWelcome screen can open this overlay), use those; otherwise own it
  // locally so the standalone web LandingPage is unchanged.
  const [signupOpenLocal, setSignupOpenLocal] = React.useState(false);
  const signupOpen = signupOpenProp ?? signupOpenLocal;
  const setSignupOpen = setSignupOpenProp ?? setSignupOpenLocal;

  // Open the focused sign-in modal in Sign In mode — for returning coaches.
  const openSignIn = () => { setAuthMode('signin'); setAuthError(''); setShowReset(false); setSignupOpen(false); setSignInOpen(true); };
  // Open the signup overlay in Create Account mode — for new coaches.
  // `signup_opened` fires HERE rather than on any individual button, so every CTA
  // on the page feeds one denominator and the campaign banner's contribution can
  // be measured against the rest of the page instead of in isolation.
  const openCreateAccount = () => {
    track('signup_opened');
    setAuthMode('signup'); setAuthError(''); setShowReset(false); setSignupOpen(true);
  };

  // Top of funnel. Everything else in the event union fires only once a coach has
  // an account, so without this there is no denominator for anything on this page.
  React.useEffect(() => { track('landing_viewed'); }, []);

  // NOTE: the "Why 72?" story section was removed on 2026-09-18 along with the
  // banner link that scrolled to it, so the IntersectionObserver that fired
  // `campaign_story_viewed` and the scrollToWhy72 handler are gone too. The event
  // remains in the analytics union in src/lib/analytics.ts deliberately: it has
  // already been emitted in production, and deleting the name would orphan the
  // history rather than tidy it. Nothing emits it now.

  // Terms/Privacy fine print shown at the point of account creation — a link
  // to these is expected at signup (app-store review + privacy regimes), and
  // the page footer alone isn't visible inside the focused auth overlays.
  const legalLine = (
    <p style={{ textAlign: 'center', fontSize: 11, lineHeight: 1.5, color: GS.ink3 }}>
      By continuing, you agree to our{' '}
      <button onClick={onShowTerms} style={{ color: GS.ink2, fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0, font: 'inherit' }}>Terms</button>
      {' '}and{' '}
      <button onClick={onShowPrivacy} style={{ color: GS.ink2, fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0, font: 'inherit' }}>Privacy Policy</button>.
    </p>
  );

  // "Sign in with Apple" — required by the App Store alongside Google, and shown
  // on web too so a coach who signed up with Apple on iOS can also log into the
  // website. Styled to Apple's guidelines (solid black, Apple logo).
  const appleButton = onAppleLogin ? (
    <button
      onClick={onAppleLogin}
      style={{ width: '100%', padding: 12, background: '#000', color: '#fff', borderRadius: 12, fontWeight: 900, fontSize: 14, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}
    >
      <svg width="16" height="16" viewBox="0 0 384 512" fill="#fff" aria-hidden="true">
        <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
      </svg>
      Continue with Apple
    </button>
  ) : null;

  return (
    <div style={{ minHeight: '100vh', background: GS.bg, color: GS.ink, overflowX: 'hidden' }}>
      {/* ── Top nav ─────────────────────────────────────────────── */}
      {/* Dark to match the hero below it — a light nav over the dark hero
          would read as a seam. Translucent + blur so it stays legible once
          the page scrolls onto the light sections. */}
      <nav style={{ position: 'sticky', top: 0, zIndex: 30, background: 'rgba(20,26,33,0.85)', backdropFilter: 'blur(12px)', borderBottom: `1px solid rgba(255,255,255,0.08)` }} className="safe-top">
        <div className="max-w-6xl mx-auto px-5" style={{ height: 70, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
            <img src="/dugout-mark.png" alt="Dugout" style={{ width: 32, height: 32, borderRadius: 9, boxShadow: `0 4px 14px ${GS.greenGlow}`, objectFit: 'cover' }} />
            <span style={{ fontSize: 19, fontWeight: 900, fontStyle: 'italic', letterSpacing: '0.02em', color: '#fff' }}>DUGOUT</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={onTryDemo} className="hidden sm:block" style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.65)', padding: '8px 14px', background: 'none', border: 'none', cursor: 'pointer' }}>
              Demo
            </button>
            <button onClick={openSignIn} style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#fff', border: `1px solid rgba(255,255,255,0.22)`, borderRadius: 10, padding: '9px 16px', background: 'rgba(255,255,255,0.06)', cursor: 'pointer' }}>
              Sign In
            </button>
          </div>
        </div>
      </nav>

      {/* ── Hero ────────────────────────────────────────────────────
          DARK "under the lights" hero. The rest of the page stays on the
          light slate canvas — this section goes near-black on purpose so the
          green glow and the (white) App Store badge actually carry energy.
          A white canvas can't feel electric; stadium light can. Copy is cut
          to a single promise line — the feature strip below does the detail. */}
      <header style={{ position: 'relative', overflow: 'hidden', background: `linear-gradient(165deg, #1B2530 0%, ${GS.ink} 55%, #0D1218 100%)` }}>
        {/* stadium-light glows: a green key light behind the headline and a
            second bloom behind the product card, so the dark reads as lit,
            not empty. Sized generously — small glows just look like smudges. */}
        <div style={{ position: 'absolute', top: -340, left: -140, width: 1100, height: 1000, background: `radial-gradient(ellipse at center, rgba(16,185,129,0.34), transparent 60%)`, pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: -120, right: -260, width: 900, height: 900, background: `radial-gradient(circle, rgba(16,185,129,0.22), transparent 62%)`, pointerEvents: 'none' }} />
        {/* subtle top highlight so the nav doesn't sit on a dead edge */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: 'linear-gradient(90deg, transparent, rgba(16,185,129,0.45), transparent)', pointerEvents: 'none' }} />

        <div className="relative max-w-6xl mx-auto px-5 pt-12 sm:pt-16 pb-16 sm:pb-20 grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          {/* Left: copy + CTAs */}
          <div>
            {/* "Win your ___" — the blank is a hand-operated scoreboard that
                rolls through six definitions of winning and rests on
                "season." Each fill changes what the verb means (defeat /
                win over / develop / manage), so the rotation demonstrates
                the plurality that the line beneath it then states. */}
            <h1 style={{ fontSize: 'clamp(38px, 5.6vw, 68px)', lineHeight: 1, letterSpacing: '-0.03em', fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase', color: '#fff', margin: 0 }}>
              Win your
            </h1>
            <div style={{ marginTop: 14 }}>
              <SplitFlapWord />
            </div>

            <p style={{ fontSize: 18, lineHeight: 1.5, color: 'rgba(255,255,255,0.72)', maxWidth: 440, fontWeight: 500, marginTop: 22 }}>
              Not all wins show up in the standings.
            </p>
            {/* Sibling, not nested: a <p> inside a <p> is invalid HTML — the
                browser auto-closes the outer one and the second sentence
                loses the color and max-width set above.

                This is the one line that has to answer "what IS this?" for a
                coach who arrives cold from the App Store. Named nouns
                (lineups, pitch counts, GameChanger) rather than abstractions,
                because "spot trends / actionable insights" is the register
                every competitor writes in and it tells a coach nothing.

                "Keep scoring in GameChanger" is doing specific work: a coach
                arriving cold assumes a rival scorebook and bounces. Naming the
                behaviour they get to KEEP defuses that, where a vaguer
                "works with" both under-reassures and risks implying live sync.
                Dugout imports a PDF/CSV after the game — never sync, stream or
                real-time language here (see docs/claude-context/CLAUDE-GameChanger.md). */}
            <p style={{ fontSize: 16, lineHeight: 1.55, color: 'rgba(255,255,255,0.60)', maxWidth: 440, fontWeight: 500, marginTop: 12 }}>
              The app for youth baseball coaches: lineups, pitch counts, and fair playing time. Keep scoring in GameChanger. Bring the scorebook here and Dugout turns a season of at-bats into a read on every player.
            </p>

            {/* Primary action row — the App Store badge leads on equal footing
                with sign-up, because "it's a real app you can download" IS the
                credibility. Wraps to one-per-row on narrow screens. */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginTop: 26 }}>
              <AppStoreBadge height={56} variant="white" />
              <button
                onClick={openCreateAccount}
                style={{ padding: '0 28px', height: 56, background: `linear-gradient(150deg, ${GS.green}, ${GS.green2})`, color: '#FFFFFF', borderRadius: 12, fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase', fontSize: 15, border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: `0 10px 34px rgba(16,185,129,0.35)` }}
              >
                Sign up free
              </button>
            </div>

            {/* Secondary, deliberately quiet — the demo is for skeptics, not
                the main path now that there's a real app to download. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 18, flexWrap: 'wrap' }}>
              <button
                onClick={onTryDemo}
                disabled={demoLoading}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fff', fontWeight: 800, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 7, padding: 0, opacity: demoLoading ? 0.6 : 1 }}
              >
                {demoLoading ? (
                  <><span className="animate-spin" style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.25)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block' }} /> Loading demo…</>
                ) : (
                  <><Play style={{ width: 14, height: 14 }} fill="#fff" /> Try the live demo</>
                )}
              </button>
            </div>
            {demoError && <p style={{ color: GS.red, fontSize: 12, fontWeight: 500, marginTop: 10 }}>{demoError}</p>}
          </div>

          {/* Right: the xOVR trendline — a lit screen floating in the dark
              hero. This replaced a live-game card (pitch count, "Top 4th"),
              which argued game-day: the exact positioning the section below
              this one rejects, and the one claim every competitor matches.
              A line rising across eleven games is the season thesis drawn as
              a shape. Numbers are real measured data from a 2026 player.
              Purely presentational — nothing here reads or runs the xOVR
              engine. */}
          {/* Shown on phones too. This card is the page's best answer to "what
              does this actually do," and iOS-launch traffic arrives mostly on
              a phone — hiding it below lg meant the entire mobile hero was a
              headline over empty space. */}
          <div className="relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              style={{ position: 'relative', background: GS.panel, border: `1px solid rgba(255,255,255,0.10)`, borderRadius: 22, overflow: 'hidden', boxShadow: '0 40px 90px -20px rgba(0,0,0,0.75), 0 0 90px -18px rgba(16,185,129,0.55)' }}
            >
              {/* head — the unit of time stated in the chrome, not "Top 4th" */}
              <div style={{ padding: '16px 20px', borderBottom: `1px solid ${GS.line}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 13, fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase' }}>Ryan Davis <span style={{ color: GS.ink3 }}>· #8</span></span>
                <span style={{ fontSize: 10, fontWeight: 900, letterSpacing: '0.14em', textTransform: 'uppercase', color: GS.ink3 }}>11 games</span>
              </div>

              {/* scout → blended, the arc stated as numbers before it's drawn */}
              <div style={{ padding: '20px 20px 6px', display: 'flex', alignItems: 'center', gap: 14 }}>
                <div>
                  <span style={{ fontSize: 30, fontWeight: 900, fontStyle: 'italic', lineHeight: 1, color: GS.ink3 }}>86</span>
                  <span style={{ display: 'block', fontSize: 8, fontWeight: 900, letterSpacing: '0.14em', textTransform: 'uppercase', color: GS.ink3, marginTop: 3 }}>Scout</span>
                </div>
                <ArrowRight style={{ width: 20, height: 20, color: GS.ink3, flexShrink: 0 }} />
                <div>
                  <span style={{ fontSize: 38, fontWeight: 900, fontStyle: 'italic', lineHeight: 1, color: GS.greenInk }}>95</span>
                  {/* NOT textTransform:uppercase — that renders the metric as
                      "XOVR". The lowercase x is the name. */}
                  <span style={{ display: 'block', fontSize: 8, fontWeight: 900, letterSpacing: '0.14em', color: GS.greenInk, marginTop: 3 }}>xOVR</span>
                </div>
                <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, background: GS.greenSoft, border: `1px solid ${GS.green}`, borderRadius: 999, padding: '6px 12px', fontSize: 12, fontWeight: 900, color: GS.greenInk }}>
                  <TrendingUp style={{ width: 14, height: 14 }} /> +9
                </span>
              </div>

              <XovrTrendline />

              {/* The whole product in one sentence. No jargon: a coach who has
                  never heard of xOVR still understands what happened. */}
              <p style={{ padding: '4px 20px 20px', fontSize: 14, lineHeight: 1.5, color: GS.ink, fontWeight: 500 }}>
                You rated him 86 in March. Eleven games later, his stats say 95.
              </p>
            </motion.div>
          </div>
        </div>
      </header>

      {/* ── The season argument ─────────────────────────────────────
          Replaces the old six-icon feature strip, which read as the
          same ALL-CAPS spec sheet every competitor in this category
          runs. This is the positioning claim instead: every other app
          in the market is a game-day tool whose unit of time is one
          game. Ours is the season. Written as a lived moment first
          (the register competitors don't write in), claim second. */}
      <section style={{ borderTop: `1px solid ${GS.line}`, background: GS.panel }}>
        <div className="max-w-3xl mx-auto px-5 py-24">
          <span style={{ ...eyebrow, display: 'inline-block', marginBottom: 18 }}>The long game</span>
          <p style={{ fontSize: 'clamp(21px, 2.6vw, 27px)', lineHeight: 1.4, color: GS.ink, fontWeight: 500, letterSpacing: '-0.01em' }}>
            It's the third inning and you're trying to remember when you pulled this pitcher last time he threw. 
            You know you knew this. It was in your head on the drive home, but now it's gone.
          </p>
          <p style={{ fontSize: 17, lineHeight: 1.6, color: GS.ink2, marginTop: 22, fontWeight: 500 }}>
            Most coaching apps help you get through Saturday. Dugout is built for the part
            that takes longer than that. Every game you bring in makes the next one sharper:
            ratings that correct themselves against real at-bats, arms tracked across a whole
            weekend, and an automatic scouting report on all your prior opponents.
          </p>
          <p style={{ fontSize: 17, lineHeight: 1.6, color: GS.ink, marginTop: 16, fontWeight: 700 }}>
            By the 5th game it knows things about your team that you'd need a staff to see.
          </p>
        </div>
      </section>

      {/* ── GameChanger companion — post-game import (NOT live sync) ──
          Answers the "do I have to re-enter my stats?" objection for the
          ~every coach who already scores in GameChanger. Copy is deliberately
          import/after-the-game language only — Dugout reads an exported
          scorebook PDF or CSV; there is no API/stream/auto-pull. Plain canvas
          to keep the canvas → band rhythm with the Free-toolkit band below. */}
      <section className="max-w-6xl mx-auto px-5 py-24">
        <div className="text-center mb-12">
          <span style={{ ...eyebrow, display: 'inline-block', marginBottom: 12 }}>Take Your Stats Up A Notch</span>
          <h2 style={{ fontSize: 'clamp(32px, 6vw, 44px)', letterSpacing: '-0.02em', fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase', marginTop: 10, lineHeight: 1.02 }}>
            You already have the data.<br /><span style={{ color: GS.greenInk }}>Put it to work.</span>
          </h2>
          <p style={{ color: GS.ink2, marginTop: 14, fontSize: 15, lineHeight: 1.55, maxWidth: 620, marginLeft: 'auto', marginRight: 'auto' }}>
            Don't reinvent the wheel... keep scoring in GameChanger. After the game, a few seconds is all it takes to share your scorebook and stats with Dugout. 
            Every at-bat, pitch sequence, and data point is preserved. Instead of wading through endless spreadsheets, Dugout reads the play-by-play and shows you what it means for your team. Arms tracked, ratings corrected against real at-bats, the opposing lineup remembered, automatically ready before you set your next one.
          </p>
        </div>

        {/* Three steps, ordered because the process genuinely is sequential.
            The third card is the argument: everyone else imports to save
            typing, we import to produce intelligence. */}
        <div className="grid md:grid-cols-3 gap-5">
          {[
            {
              n: '01', icon: ClipboardList, title: 'Score the game',
              body: 'Run the game in GameChanger exactly like you do now. Every at-bat, every pitch. Nothing changes about game day.',
            },
            {
              n: '02', icon: Share, title: 'Send it over',
              body: "When the game's over, share your GameChanger scorebook straight into Dugout on iPhone, or upload the CSV export anywhere. Two taps.",
            },
            {
              n: '03', icon: Sparkles, title: 'It becomes something else',
              body: 'Dugout reads the play-by-play other apps only store. Arms tracked, ratings corrected against real at-bats, the opposing lineup remembered by jersey and slot, ready before you set your next one.',
            },
          ].map(({ n, icon: Icon, title, body }) => (
            <div key={n} style={{ background: GS.panel, border: `1px solid ${GS.line}`, borderRadius: 20, padding: 26, boxShadow: '0 1px 2px rgba(20,26,33,0.04)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div style={{ width: 46, height: 46, borderRadius: 13, background: GS.greenSoft, border: `1px solid ${GS.green}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon style={{ width: 20, height: 20, color: GS.greenInk }} />
                </div>
                <span style={{ fontSize: 12, fontWeight: 900, fontStyle: 'italic', letterSpacing: '0.08em', color: GS.ink3 }}>{n}</span>
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase' }}>{title}</h3>
              <p style={{ fontSize: 14, color: GS.ink2, lineHeight: 1.55, marginTop: 10 }}>{body}</p>
            </div>
          ))}
        </div>

        <SectionCta label="Connect your scorebook" onClick={openCreateAccount} align="center" hint="Import a season from your GameChanger file in less than a minute." />

        {/* Non-affiliation fine print — interoperability, not endorsement. */}
        <p style={{ textAlign: 'center', fontSize: 11, lineHeight: 1.5, color: GS.ink3, marginTop: 24, maxWidth: 620, marginLeft: 'auto', marginRight: 'auto' }}>
          GameChanger is a trademark of its respective owner. Dugout is an independent app and is not affiliated with, endorsed by, or sponsored by GameChanger or Dick's Sporting Goods.
        </p>
      </section>

      {/* ── What is xOVR — the signature idea, explained ──────────
          The page name-drops xOVR everywhere but never defines it. This is the
          explainer. Copy is truthful to src/lib/xovr-engine.ts: scout OVR is the
          anchor, stats shift it ±10 max, confidence scales with sample size.
          Paid tier is soft-pedaled (no price/free/gold) — the Elite section
          right below carries it. Plain canvas; two-column copy + before→after
          card mirroring the hero. */}
      <section style={{ borderTop: `1px solid ${GS.line}`, background: GS.panel }}>
        <div className="max-w-6xl mx-auto px-5 py-24 grid md:grid-cols-2 gap-10 items-center">
        {/* Left: the explanation */}
        <div>
          {/* textTransform reset so the eyebrow reads "What is xOVR", not
              "WHAT IS XOVR" — the lowercase x is the metric's name. */}
          <span style={{ ...eyebrow, display: 'inline-block', marginBottom: 12, textTransform: 'none' }}>INTRODUCING xOVR</span>
          <h2 style={{ fontSize: 'clamp(32px, 6vw, 44px)', letterSpacing: '-0.02em', fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase', lineHeight: 1.02 }}>
            An unbiased opinion.<br /><span style={{ color: GS.greenInk }}>on every player. Instantly.</span>
          </h2>
          <p style={{ color: GS.ink2, marginTop: 16, fontSize: 16, lineHeight: 1.55, maxWidth: 440 }}>
            The magic of Dugout is xOVR. Combine your subjective eye with the objective stats to make sure you don't overlook (or overvalue) any players.
          </p>

          <div style={{ marginTop: 24 }} className="space-y-4">
            {[
              { icon: ClipboardList, title: 'You scout first', body: 'You rate every player on what you see. That’s their OVR, and it stays the anchor all season.' },
              { icon: TrendingUp, title: 'Then the season starts', body: 'As the games come in, the stats either back up your eye or nudge it. A 77 scout can climb to an 87 after a dominant season, or settle to a 67 after a rough one, but never more than that. Your eye still leads.' },
              { icon: Sparkles, title: 'It flags discrepancies', body: 'When you’re under-rating the quiet player who’s secretly raking, or over-rating the preseason star who’s making a lot of loud outs, xOVR is the one that notices.' },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} style={{ display: 'flex', gap: 14 }}>
                <div style={{ width: 46, height: 46, borderRadius: 13, background: GS.greenSoft, border: `1px solid ${GS.green}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon style={{ width: 20, height: 20, color: GS.greenInk }} />
                </div>
                <div>
                  <h3 style={{ fontSize: 14, fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase' }}>{title}</h3>
                  <p style={{ fontSize: 13, color: GS.ink2, lineHeight: 1.5, marginTop: 3 }}>{body}</p>
                </div>
              </div>
            ))}
          </div>

          <p style={{ fontSize: 14, color: GS.ink2, lineHeight: 1.55, marginTop: 14, maxWidth: 440 }}>
            xOVR helps you chart each player’s growth unfold across the whole season.
          </p>
          <SectionCta label="Discover your team's OVR" onClick={openCreateAccount} hint="The average coach takes 8 minutes to rank their whole roster."/>
        </div>

        {/* Right: before → after card. Neutral scout 77 → green xOVR 87.
            Reuses the hero card's badge/pill/chip recipes for visual rhyme. */}
        <div className="relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            style={{ position: 'relative', background: GS.panel, border: `1px solid ${GS.line}`, borderRadius: 22, overflow: 'hidden', boxShadow: '0 30px 70px -30px rgba(20,26,33,0.30)' }}
          >
            {/* head */}
            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${GS.line}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 12, fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase' }}>Marcus Bell <span style={{ color: GS.ink3 }}>· 2B</span></span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 10, fontWeight: 900, letterSpacing: '0.14em', textTransform: 'uppercase', color: GS.greenInk }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: GS.green, boxShadow: `0 0 8px ${GS.green}`, display: 'inline-block' }} /> Full season
              </span>
            </div>
            {/* transform row: neutral scout → arrow → green xOVR */}
            <div style={{ padding: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ width: 72, height: 72, borderRadius: 17, background: GS.panel2, border: `1px solid ${GS.line}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 26, fontWeight: 900, fontStyle: 'italic', lineHeight: 1, color: GS.ink }}>77</span>
                <span style={{ fontSize: 8, fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase', color: GS.ink3, marginTop: 3 }}>Scout OVR</span>
              </div>
              <ArrowRight style={{ width: 26, height: 26, color: GS.greenInk, flexShrink: 0 }} />
              <div style={{ width: 72, height: 72, borderRadius: 17, background: `linear-gradient(150deg, ${GS.green}, ${GS.green2})`, color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', boxShadow: `0 8px 26px ${GS.greenGlow}` }}>
                <span style={{ fontSize: 28, fontWeight: 900, fontStyle: 'italic', lineHeight: 1 }}>87</span>
                {/* textTransform is inherited from an ancestor here — reset it
                    explicitly or the metric renders as "XOVR". */}
                <span style={{ fontSize: 8, fontWeight: 900, letterSpacing: '0.14em', marginTop: 3, textTransform: 'none' }}>xOVR</span>
              </div>
            </div>
            {/* swing pill */}
            <div style={{ margin: '0 20px 16px', background: GS.greenSoft, border: `1px solid ${GS.green}`, borderRadius: 14, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <TrendingUp style={{ width: 18, height: 18, color: GS.greenInk, flexShrink: 0 }} />
              <p style={{ fontSize: 13, lineHeight: 1.4, color: GS.ink, fontWeight: 500 }}>
                <strong style={{ fontWeight: 800 }}>+10</strong> based on 5 game on-base streak and decreased strike out rate.
              </p>
            </div>
            {/* stat chips — the drivers */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, padding: '0 20px 20px' }}>
              {[['AVG', '.412'], ['OPS', '1.040'], ['PA', '31']].map(([k, v]) => (
                <div key={k} style={{ background: GS.panel2, border: `1px solid ${GS.line}`, borderRadius: 13, padding: '14px 0', textAlign: 'center' }}>
                  <p style={{ fontSize: 19, fontWeight: 900, fontStyle: 'italic', color: GS.greenInk }}>{v}</p>
                  <p style={{ fontSize: 8, fontWeight: 900, letterSpacing: '0.14em', textTransform: 'uppercase', color: GS.ink3, marginTop: 2 }}>{k}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
        </div>
      </section>

      {/* ── Fairness ────────────────────────────────────────────────
          The floor claim in the positioning spine (development is the
          ceiling). Sits directly after the season argument because that's
          what makes it credible: fairness measured over one game is a
          scorekeeping trick, fairness measured over a season is a promise.

          ⚠️ POSITIONING CONSTRAINT — read before editing this section.
          Dugout serves BOTH rec coaches (who need minimum-play and
          no-consecutive-sit compliance) and competitive travel coaches (who
          will churn instantly if they think an app will force them to start
          someone). This section must never imply the engine overrides the
          coach. It doesn't:
            - forceBenchIds seeds the bench BEFORE fairness runs, so the
              coach's explicit picks always win (lineup-engine.ts ~619).
            - noConsecutiveBench is a PREFERENCE that bends: the engine tops
              up the bench from just-sat players when the roster forces it
              (~632), and the in-file comment says a leak just "alerts the
              coach."
            - fairness-check.ts is analyzeFairness() returning
              FairnessFinding[] — findings and early warnings, not blocks.
          The only thing lineup-engine.ts calls a "hard rule" is catcher
          protection (~649). The previous copy applied that phrase to bench
          rotation, which was simply wrong. Frame is ALWAYS: it tracks and
          tells you, you decide. */}
      <section style={{ borderTop: `1px solid ${GS.line}` }}>
        <div className="max-w-6xl mx-auto px-5 py-24 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <span style={{ ...eyebrow, display: 'inline-block', marginBottom: 12 }}>FAIRNESS</span>
            <h2 style={{ fontSize: 'clamp(32px, 6vw, 44px)', letterSpacing: '-0.02em', fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase', lineHeight: 1.02 }}>
              You still make<br /><span style={{ color: GS.greenInk }}>the call.</span>
            </h2>
            <p style={{ color: GS.ink2, marginTop: 16, fontSize: 16, lineHeight: 1.55, maxWidth: 460 }}>
              Every coach means to keep it even. Then it's the fifth inning of a one-run
              game and you genuinely cannot remember who sat last week. Dugout keeps that
              count for you across every game of the season, and tells you what it sees.
              The lineup is still yours.
            </p>

            <div style={{ marginTop: 24 }} className="space-y-4">
              {[
                { icon: Shield, title: 'It tracks, you decide', body: 'Dugout flags who is short on innings or sitting back-to-back. It never locks a lineup or overrules a starter you picked.' },
                { icon: Repeat, title: 'Your league rules, watched', body: 'Set minimum play time or no-consecutive-sit once, and Dugout keeps an eye on them every inning so you are not counting in your head.' },
                { icon: Users, title: 'Neutral bench rotation', body: 'When Dugout does suggest the bench, it ignores OVR completely. A weaker player never carries extra bench time because the app thinks they are weaker.' },
              ].map(({ icon: Icon, title, body }) => (
                <div key={title} style={{ display: 'flex', gap: 14 }}>
                  <div style={{ width: 46, height: 46, borderRadius: 13, background: GS.greenSoft, border: `1px solid ${GS.green}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon style={{ width: 20, height: 20, color: GS.greenInk }} />
                  </div>
                  <div>
                    <h3 style={{ fontSize: 14, fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase' }}>{title}</h3>
                    <p style={{ fontSize: 13, color: GS.ink2, lineHeight: 1.5, marginTop: 3 }}>{body}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Speaks to BOTH coaches in two sentences: the rec coach who has
                a rule to satisfy, and the travel coach who plays his best
                nine and just wants the receipt when a parent asks. Neither is
                asked to be the other. */}
            <p style={{ fontSize: 14, color: GS.ink, lineHeight: 1.55, marginTop: 22, maxWidth: 460, fontWeight: 700 }}>
              Rec team with a minimum-play rule, or travel team playing to win? Set it your way.
              Either way, when a parent asks why their kid sat, you have the real answer.
            </p>
            <SectionCta label="See your bench ledger" onClick={openCreateAccount} hint="Every fairness rule is a toggle. Leave them all off if you want." />
          </div>

          {/* Right: the receipt. A season bench ledger is the artifact a coach
              actually wants in that parking-lot conversation — evened-out
              numbers, with the one player who is owed time flagged. */}
          <div className="relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
              style={{ position: 'relative', background: GS.panel, border: `1px solid ${GS.line}`, borderRadius: 22, overflow: 'hidden', boxShadow: '0 30px 70px -30px rgba(20,26,33,0.30)' }}
            >
              <div style={{ padding: '16px 20px', borderBottom: `1px solid ${GS.line}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12, fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase' }}>Bench innings <span style={{ color: GS.ink3 }}>· season</span></span>
                <span style={{ fontSize: 10, fontWeight: 900, letterSpacing: '0.14em', textTransform: 'uppercase', color: GS.ink3 }}>11 games</span>
              </div>

              <div style={{ padding: '6px 20px 4px' }}>
                {[
                  ['Marcus Bell', '#4', 8, false],
                  ['Ryan Davis', '#8', 8, false],
                  ['Eli Barnes', '#12', 9, false],
                  ['Jordan Cruz', '#7', 9, false],
                  ['Sam Whitaker', '#3', 11, true],
                ].map(([name, num, innings, owed]) => (
                  <div key={name as string} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0', borderBottom: `1px solid ${GS.line}` }}>
                    <span style={{ fontSize: 13, fontWeight: 800, color: GS.ink, flex: 1 }}>
                      {name as string} <span style={{ color: GS.ink3, fontWeight: 700 }}>{num as string}</span>
                    </span>
                    {owed as boolean && (
                      <span style={{ fontSize: 9, fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase', color: GS.greenInk, background: GS.greenSoft, border: `1px solid ${GS.green}`, borderRadius: 999, padding: '3px 9px' }}>
                        Owed time
                      </span>
                    )}
                    {/* bar length is proportional to bench innings; the player
                        who has sat most is the one the engine plays next */}
                    <span style={{ width: 92, height: 7, borderRadius: 999, background: GS.panel2, border: `1px solid ${GS.line}`, overflow: 'hidden', flexShrink: 0 }}>
                      <span style={{ display: 'block', height: '100%', width: `${((innings as number) / 11) * 100}%`, background: owed ? GS.green : GS.ink3, borderRadius: 999 }} />
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 900, fontVariantNumeric: 'tabular-nums', color: owed ? GS.greenInk : GS.ink2, width: 18, textAlign: 'right' }}>{innings as number}</span>
                  </div>
                ))}
              </div>

              <div style={{ margin: '12px 20px 20px', background: GS.greenSoft, border: `1px solid ${GS.green}`, borderRadius: 14, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <Shield style={{ width: 18, height: 18, color: GS.greenInk, flexShrink: 0 }} />
                {/* Reads as an observation, not an instruction. "Start him
                    next game" is the corrective register the bench-coach
                    voice rule forbids, and it's exactly what makes a
                    competitive coach think the app is going to boss him. */}
                <p style={{ fontSize: 13, lineHeight: 1.4, color: GS.ink, fontWeight: 500 }}>
                  Sam has sat the most this season. Worth a look when you set the next lineup.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── The Game Plan — pregame command center + demo video ──
          Copy must match the VIDEO (public/dugout-demo.mp4), which is a
          PREGAME walkthrough: home screen, plan the next game, assign a
          pitcher, get the "Pitcher on Mandatory Rest" warning, pick a
          different arm, build the lineup, print the card.

          The old headline ("Run the game from one screen") was wrong twice:
          it described a live-game screen the video never shows, and it
          implied Dugout replaces the scorebook, which contradicts the
          GameChanger companion section above. Dugout's claim is the hour
          BEFORE first pitch, not the scorekeeping during it. The rest
          warning is real: LineupBuilder.tsx checks pitchingLog eligibleDate
          and warns without blocking.

          This is the page's strongest section by feature density — the Game
          Plan screen is what a coach would otherwise keep in three
          spreadsheets and a group text. Positioned as capability ("every
          decision on one screen"), never as admin relief. */}
      <section className="max-w-6xl mx-auto px-5 py-24">
        <div className="text-center mb-10">
          <span style={{ ...eyebrow, display: 'inline-block', marginBottom: 12 }}>The game plan</span>
          <h2 style={{ fontSize: 'clamp(32px, 6vw, 44px)', letterSpacing: '-0.02em', fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase', marginTop: 10, lineHeight: 1.02 }}>
            Every decision before<br /><span style={{ color: GS.greenInk }}>first pitch. One screen.</span>
          </h2>
          {/* The spreadsheet is the VILLAIN here, never the hero. Leading on
              "kills your spreadsheets" would drop us into the admin-relief
              lane Fungo and CoachSkip already own (see CLAUDE-Marketing.md,
              Finding 4: never lead with a time-saved promise). The claim is
              capability, not relief: a spreadsheet physically cannot hold
              these six things at once, twenty minutes before first pitch. */}
          <p style={{ color: GS.ink2, marginTop: 14, fontSize: 15, lineHeight: 1.6, maxWidth: 620, marginLeft: 'auto', marginRight: 'auto' }}>
            Who's rested, who's owed innings, what the other dugout did to you last time, and the batting order that fits all three. A spreadsheet can hold last week's numbers. It can't tell you any of this while you're standing at the fence.
          </p>
        </div>

        {/* The six panes of the Game Plan screen, named. This is the "kills the
            spreadsheet" argument made by enumeration rather than by claim —
            each one is a real tab in PlanGameView.tsx (Bullpen, Scouting,
            Fairness, Lineup scenarios, Defense) plus the GC lineup-card
            import (src/lib/pdf/lineupCardParser.ts). Six is deliberate: it's
            past the number a person can juggle on paper, which is the point. */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
          {[
            { title: 'Bullpen status', body: 'Every arm’s pitch count, rest days, and who’s legal to throw today under your league’s rules.' },
            { title: 'Opponent book', body: 'What they did to you last meeting. Their pitchers, their danger bats, who runs on you.' },
            { title: 'Fair playing time', body: 'The season bench ledger at a glance, so you know who’s owed innings before you set the order.' },
            { title: 'Lineup scenarios', body: 'Seven batting orders on tap: get baserunners, wear down the pitcher, minimize strikeouts, fairness.' },
            { title: 'Defensive scenarios', body: 'Build the field position by position, with each player’s rating at that spot in front of you.' },
            { title: 'Starting pitcher', body: 'Pick the arm and the lineup builds around it, with the rest warning if that arm isn’t ready.' },
          ].map(({ title, body }) => (
            <div key={title} style={{ background: GS.panel, border: `1px solid ${GS.line}`, borderRadius: 16, padding: 20 }}>
              <h3 style={{ fontSize: 13, fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase', color: GS.greenInk }}>{title}</h3>
              <p style={{ fontSize: 13.5, color: GS.ink2, lineHeight: 1.5, marginTop: 8 }}>{body}</p>
            </div>
          ))}
        </div>

        {/* Video frame — inline <video> when a URL is supplied; otherwise a poster
            that launches the live interactive demo (a better CTA than a still). */}
        <div style={{ position: 'relative', borderRadius: 22, overflow: 'hidden', border: `1px solid ${GS.line}`, background: demoVideoUrl ? '#FFFFFF' : GS.ink, boxShadow: '0 30px 70px -30px rgba(20,26,33,0.35)', aspectRatio: demoVideoUrl ? '1652 / 1044' : '16 / 9' }}>
          {demoVideoUrl ? (
            <video
              src={demoVideoUrl}
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            />
          ) : (
            <button
              onClick={onTryDemo}
              disabled={demoLoading}
              aria-label="Try the live demo"
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none', cursor: 'pointer', background: `radial-gradient(circle at 50% 45%, ${GS.greenGlow}, transparent 60%), ${GS.ink}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 18 }}
            >
              <span style={{ width: 76, height: 76, borderRadius: '50%', background: `linear-gradient(150deg, ${GS.green}, ${GS.green2})`, boxShadow: `0 8px 30px ${GS.greenGlow}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Play style={{ width: 30, height: 30, color: '#fff', marginLeft: 4 }} fill="#fff" />
              </span>
              <span style={{ fontSize: 13, fontWeight: 900, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#fff' }}>
                {demoLoading ? 'Loading demo…' : 'Try the live demo'}
              </span>
              <span style={{ fontSize: 11, fontWeight: 600, color: GS.ink3 }}>No signup. Runs a real game in your browser</span>
            </button>
          )}
        </div>
        {/* Only when a video is playing — the poster state already carries its
            own play-button CTA, and two demo prompts would compete. */}
        {/* GC lineup-card import. Given its own strip because it's the single
            most concrete proof that the companion positioning is real: a coach
            who already sets lineups in GC doesn't have to retype anything.
            Truthful to src/lib/pdf/lineupCardParser.ts — it reads the batting
            slot, jersey number and starting position off the GC lineup card
            PDF and matches players to the Dugout roster by jersey, falling
            back to a last-name match. Import language only, never "sync". */}
        <div style={{ background: GS.panel, border: `1px solid ${GS.line}`, borderRadius: 20, padding: 'clamp(22px, 4vw, 32px)', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 20, marginBottom: 8 }}>
          <div style={{ width: 46, height: 46, borderRadius: 13, background: GS.greenSoft, border: `1px solid ${GS.green}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <ClipboardList style={{ width: 20, height: 20, color: GS.greenInk }} />
          </div>
          <div style={{ flex: '1 1 320px', minWidth: 0 }}>
            <h3 style={{ fontSize: 16, fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase' }}>Already made your lineup in GameChanger?</h3>
            <p style={{ fontSize: 14, color: GS.ink2, lineHeight: 1.55, marginTop: 7 }}>
              Import the lineup card and Dugout fills in the batting order and every starting position for you, matched to your roster by jersey number. Nothing to retype.
            </p>
          </div>
        </div>

        {/* Hint describes what the video just showed (lineup building, which
            is free-tier), not Elite — the old "Experience everything in Dugout
            Elite" promised a different product than the one on screen. */}
        {demoVideoUrl && <SectionCta label="Try the live demo" onClick={onTryDemo} align="center" hint="Build a lineup with a real roster. No signup required." />}
      </section>

      {/* ── Pricing, part 1 of 2: free forever ──────────────────────
          The bottom border is deliberately absent — this band and the Elite
          section below it are ONE money moment (free floor, then the
          upgrade), so they share a background and run together. A divider
          here would split the page's only pricing beat into two. */}
      <section style={{ background: GS.panel, borderTop: `1px solid ${GS.line}` }}>
        <div className="max-w-6xl mx-auto px-5 pt-24 pb-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <span style={{ ...eyebrow, display: 'inline-block', marginBottom: 12 }}>The everyday toolkit · Free</span>
            <h2 style={{ fontSize: 'clamp(28px, 5vw, 38px)', letterSpacing: '-0.02em', fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase', lineHeight: 1.02 }}>
              Everything game<br />day needs.<br /><span style={{ color: GS.greenInk }}>Free.</span>
            </h2>
            <p style={{ color: GS.ink2, marginTop: 16, fontSize: 16, lineHeight: 1.55, maxWidth: 440 }}>
              Build the lineup, track pitch counts and rest days, keep playing time fair. The work every coach does every week. It's <strong style={{ color: GS.ink, fontWeight: 700 }}>free for every coach.</strong> Start a season tonight and reclaim the hours of lineup and stat work each week.
            </p>
          </div>
          <div style={{ background: GS.bg, border: `1px solid ${GS.line}`, borderRadius: 18, padding: 24 }}>
            {[
              'Drag-and-drop lineup builder',
              'Fair playing-time rotations &amp; bench rules',
              'Pitch counts &amp; rest-day tracking',
              'In-game position &amp; innings tracking',
              'Printable lineup cards',
              'Season schedule &amp; calendar import',
            ].map((f, i, arr) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0', borderBottom: i < arr.length - 1 ? `1px solid ${GS.line}` : 'none' }}>
                <span style={{ width: 22, height: 22, borderRadius: '50%', background: GS.green, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Check style={{ width: 13, height: 13, color: '#fff' }} strokeWidth={3} />
                </span>
                <span style={{ fontSize: 14, fontWeight: 600, color: GS.ink }} dangerouslySetInnerHTML={{ __html: f }} />
              </div>
            ))}
            <div style={{ marginTop: 18, textAlign: 'center', fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: GS.greenInk }}>
              Included and unlimited in all Dugout accounts for free.
            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing, part 2 of 2: Elite ─────────────────────────────
          Shares the free band's background above it (no divider between)
          so free-floor → upgrade reads as a single pricing beat. */}
      <section style={{ background: GS.panel, borderBottom: `1px solid ${GS.line}` }}>
        <div className="max-w-6xl mx-auto px-5 pb-24">
        <div className="text-center mb-12">
          <span style={{ ...eyebrow, display: 'inline-block', marginBottom: 12 }}>Dugout Elite</span>
          <h2 style={{ fontSize: 'clamp(32px, 6vw, 44px)', letterSpacing: '-0.02em', fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase', marginTop: 10 }}>Join the ranks of Elite Coaches</h2>
          <p style={{ color: GS.ink2, marginTop: 12, fontSize: 15, maxWidth: 600, marginLeft: 'auto', marginRight: 'auto' }}>
            Upgrade to Dugout Elite to unlock all your data's potential. Know your opponent before first pitch, get through pool play on Saturday with arms to spare Sunday, and reward the players who are actually developing.
          </p>
        </div>

        {/* Three pillars: the weekend, the opponent, the player. Ordered by
            what a competitive coach feels first — surviving the tournament,
            then out-preparing the other dugout, then long-season development. */}
        <div className="grid md:grid-cols-3 gap-5 mb-8">
          {[
            {
              title: 'Win the weekend',
              body: 'No matter the tournament format, Tournament Mode tracks bullpen counts and tournament specific rest rules across the whole weekend. It even handles guest players.',
              points: ['Bullpen counts across the weekend', 'Guest player management', 'Rest-day math done for you'],
            },
            {
              title: 'Unlock Your Stats',
              body: 'Dugout Elite reads your GameChanger stats and scorebook and turns it into a full season of stats, including advanced metrics. It even builds a book on every opponent you face.',
              points: ['One click importing', 'Scout AI insights per game', 'Opponent scouting book built automatically'], 
            },
            {
              title: 'Develop every player',
              body: 'xOVR blends your stats with your own scouting so you see who is really developing, who is ready for more, and who needs reps. Plus Smart Scout AI insights after every game.',
              points: ['xOVR: stats meet scouting', 'Smart Scout AI insights', 'Assistant-coach collaboration'],
            },
          ].map((c, i) => (
            <div key={i} style={{ background: GS.panel, border: `1px solid ${GS.line}`, borderRadius: 20, padding: 26, boxShadow: '0 1px 2px rgba(20,26,33,0.04)' }}>
              <h3 style={{ fontSize: 18, fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase' }}>{c.title}</h3>
              <p style={{ fontSize: 14, color: GS.ink2, lineHeight: 1.55, marginTop: 10 }}>{c.body}</p>
              <div style={{ marginTop: 16, borderTop: `1px solid ${GS.line}`, paddingTop: 16 }}>
                {c.points.map((p, j) => (
                  <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
                    <span style={{ width: 18, height: 18, borderRadius: '50%', background: GS.greenSoft, border: `1px solid ${GS.green}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Check style={{ width: 11, height: 11, color: GS.greenInk }} strokeWidth={3} />
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: GS.ink }} dangerouslySetInnerHTML={{ __html: p }} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Price + CTA */}
        <div style={{ background: GS.ink, borderRadius: 20, padding: '28px 24px', textAlign: 'center' }}>
          <span style={{ display: 'inline-block', marginBottom: 14, fontSize: 11, fontWeight: 900, letterSpacing: '0.12em', textTransform: 'uppercase', color: GS.green, background: GS.greenSoft, border: `1px solid ${GS.green}`, borderRadius: 20, padding: '6px 14px' }}>
            {SEPTEMBER_PUSH && PASSES_SOLD_OUT
              ? 'Season passes · Sold out'
              : SEPTEMBER_PUSH
                ? 'September only · 72 season passes'
                : 'Start with a 14-day free trial'}
          </span>
          <div style={{ fontSize: 'clamp(28px, 5vw, 36px)', fontWeight: 900, fontStyle: 'italic', color: '#fff', lineHeight: 1 }}>
            {SEPTEMBER_PUSH && !PASSES_SOLD_OUT ? (
              <>Free<span style={{ fontSize: 16, color: GS.ink3 }}> through March 1</span></>
            ) : (
              <>$4.99<span style={{ fontSize: 16, color: GS.ink3 }}>/mo billed annually</span></>
            )}
          </div>
          {/* Full terms, spelled out. Apple 3.1.2 wants the renewal price and
              billing period stated wherever a subscription is offered, and a
              coach who reads "$4.99/mo" should learn the actual charge here
              rather than at the payment sheet. $59.99/yr matches PRICES.annual
              in EliteUpgradeModal.tsx; update both together.

              The price stays visible during the September Push on purpose: a
              coach who only discovers what Elite costs in March is a coach who
              churns in March. Free now, and honest about what "after" means. */}
          <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: GS.ink3, marginTop: 8 }}>
            {SEPTEMBER_PUSH && !PASSES_SOLD_OUT
              ? 'the whole fall season · no credit card · then $59.99/year or $9.99/mo'
              : 'billed $59.99/year after trial · or $9.99/mo · cancel anytime'}
          </p>
          <button
            onClick={openCreateAccount}
            style={{ marginTop: 18, padding: '15px 28px', background: `linear-gradient(150deg, ${GS.green}, ${GS.green2})`, color: '#fff', borderRadius: 14, fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase', fontSize: 14, border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, boxShadow: `0 8px 28px ${GS.greenGlow}` }}
          >
            {SEPTEMBER_PUSH && !PASSES_SOLD_OUT ? 'Claim your season pass' : 'Start your free trial'} <ArrowRight style={{ width: 16, height: 16 }} />
          </button>
          {/* Elite is a subscription, so the two links Apple looks for have to
              be reachable from the page that sells it. */}
          <p style={{ fontSize: 11, color: GS.ink3, marginTop: 14 }}>
            <button onClick={onShowTerms} style={{ color: GS.ink3, fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0, font: 'inherit' }}>Terms of Use</button>
            {' · '}
            <button onClick={onShowPrivacy} style={{ color: GS.ink3, fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0, font: 'inherit' }}>Privacy Policy</button>
          </p>
        </div>
        </div>
      </section>

      {/* ── Closing: trust signals + final CTA ───────────────────
          Replaces the old "headaches" grid (which repeated promises made
          above and ended the page on a downbeat). Ends on momentum: factual
          trust signals (no fabricated testimonials — none yet, pre-launch),
          then one dominant "Sign up free". Drop real coach quotes in here
          once we have them. */}
      <section className="max-w-6xl mx-auto px-5 py-24">
        <div className="text-center mb-12">
          <span style={{ ...eyebrow, display: 'inline-block', marginBottom: 12 }}>From the dugout</span>
          <h2 style={{ fontSize: 'clamp(32px, 6vw, 44px)', letterSpacing: '-0.02em', fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase', marginTop: 10 }}>Coach like you have a staff.</h2>
          <p style={{ color: GS.ink2, marginTop: 12, fontSize: 15, maxWidth: 560, marginLeft: 'auto', marginRight: 'auto' }}>
            Advance scouting, arm management, and a read on every player. The work a big program
            pays people to do, running quietly on your phone.
          </p>
        </div>

        {/* Real coach quotes, verbatim — these are GENUINE and confirmed, not
            placeholders. Do not remove. Two of them, given room, rather than
            a wall of manufactured-looking praise — the club-admin one proves
            the multi-team/evaluation use case and the Tanner one describes
            xOVR in a coach's own words ("quietly improving"), which is the
            exact promise the xOVR section makes further up. */}
        <div className="grid md:grid-cols-2 gap-5 mb-12">
          {[
            {
              quote: 'Dugout has given our club system-wide insight into the players on each team, helping us evaluate who’s ready to move up.',
              who: 'Club Admin', where: 'Kansas City, MO',
            },
            {
              quote: 'I like how the player ratings adjust after each game to help me see quickly who is quietly improving.',
              who: 'Tanner', where: 'California',
            },
          ].map(({ quote, who, where }) => (
            <figure key={who} style={{ background: GS.panel, border: `1px solid ${GS.line}`, borderRadius: 20, padding: 28, boxShadow: '0 1px 2px rgba(20,26,33,0.04)', margin: 0, display: 'flex', flexDirection: 'column' }}>
              <span aria-hidden="true" style={{ fontSize: 44, lineHeight: 0.7, fontWeight: 900, color: GS.green, fontStyle: 'italic' }}>&ldquo;</span>
              <blockquote style={{ margin: 0, fontSize: 17, lineHeight: 1.5, color: GS.ink, fontWeight: 500, marginTop: 12, flex: 1 }}>
                {quote}
              </blockquote>
              <figcaption style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${GS.line}`, fontSize: 11, fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase', color: GS.ink3 }}>
                {who} <span style={{ color: GS.green }}>·</span> {where}
              </figcaption>
            </figure>
          ))}
        </div>

        {/* ── Founder note ──────────────────────────────────────────
            NOT a testimonial, and styled so it can never be mistaken for one:
            dark ground instead of the white <figure> cards above, no green
            quote mark, and the word "founder" in the signature. Three coach-
            styled quotes where one of them is the person who built the app
            reads as manufactured social proof the moment a visitor works out
            who Dan is; an openly signed founder note is the honest form of
            the same content, and it's the one thing no competitor can copy.

            The story is true and specific on purpose: first home run OF THE
            SEASON (not team history), from a player who'd never hit one,
            recommended three games ahead. The mechanism is real — the batting
            order engine assigns slots with a per-player `reason` and its
            "Optimized" strategy balances OBP, power and contact across the
            order (src/lib/batting-order.ts), so a coach who signs up on the
            strength of this note will actually find the feature that made the
            call. xOVR is deliberately NOT name-dropped; the lineup suggestion
            is what fired, and the xOVR section above carries that explanation.

            The quote ends on "he hit the first one of the year." A closing
            line of commentary was tried and cut: the reversal ("We didn't do
            it") already lands, and explaining it afterward talked it down. */}
        <div style={{ background: GS.ink, borderRadius: 20, padding: 'clamp(26px, 5vw, 38px)', marginBottom: 56, maxWidth: 760, marginLeft: 'auto', marginRight: 'auto' }}>
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: GS.green }}>
            From the founder
          </span>
          <blockquote style={{ margin: 0, marginTop: 16, fontSize: 'clamp(17px, 2.2vw, 20px)', lineHeight: 1.55, color: '#fff', fontWeight: 500 }}>
            Three games before it happened, Dugout suggested I move an unexpected player into the
            3rd spot in the lineup, and drop our usual power hitter down. We didn't do it. He'd
            never hit a home run before. But sure enough, he hit our first one of the year.
          </blockquote>
          <div style={{ marginTop: 22, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.14)', display: 'flex', alignItems: 'center', gap: 11 }}>
            <img src="/dugout-mark.png" alt="" style={{ width: 30, height: 30, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
            {/* "Youth baseball coach" wrapped to a second line on a 390px
                phone and orphaned the last word. Shortened so the whole
                signature holds one line — the credential that matters is
                that he coaches, and "coach" carries it. */}
            <span style={{ fontSize: 11, fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.62)' }}>
              Dan <span style={{ color: GS.green }}>·</span> Founder &amp; coach
            </span>
          </div>
        </div>

        {/* Trust signals demoted to a single quiet strip — they were four
            large cards competing with the quotes for the same attention. */}
        <div className="flex flex-wrap items-center justify-center gap-x-7 gap-y-3 mb-14" style={{ fontSize: 12, fontWeight: 800, color: GS.ink2 }}>
          {/* Each of these has to be literally true and checkable. "Industry
              leading security" was an unverifiable superlative sitting next to
              two concrete claims, which undercuts both. "Your data stays yours"
              is what a coach actually wants to know and what the privacy policy
              already commits to. */}
          {[
            { icon: Shield, label: 'Your data stays yours' },
            { icon: Trophy, label: '5U through high school' },
            { icon: Check, label: 'Free tier, no expiration' },
          ].map(({ icon: Icon, label }) => (
            <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
              <Icon style={{ width: 15, height: 15, color: GS.greenInk, flexShrink: 0 }} /> {label}
            </span>
          ))}
        </div>

        {/* Final CTA — dark band for a strong close. Opens the signup overlay. */}
        <div style={{ background: GS.ink, borderRadius: 24, padding: '40px 24px', textAlign: 'center' }}>
          {/* "Win it all" only works HERE, not in the hero: by this point the
              page has defined six different wins, so "all" reads as all of
              them rather than as a championship promise. */}
          <h2 style={{ fontSize: 'clamp(28px, 5vw, 40px)', letterSpacing: '-0.02em', fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase', color: '#fff', lineHeight: 1.02 }}>
            Win it all.
          </h2>
          <p style={{ color: GS.ink3, marginTop: 12, fontSize: 15, maxWidth: 460, marginLeft: 'auto', marginRight: 'auto' }}>
            The bracket, the bullpen, the bench, and the season. Free to start, with a 14-day
            trial of Elite when you want the full picture.
          </p>
          {/* Both paths, mirroring the hero. A coach who reads the whole page
              and decides at the bottom shouldn't have to scroll back up to
              find the download — this is the page's last exit. */}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 24 }}>
            <AppStoreBadge height={54} variant="white" />
            <button
              onClick={openCreateAccount}
              style={{ padding: '0 32px', height: 54, background: `linear-gradient(150deg, ${GS.green}, ${GS.green2})`, color: '#fff', borderRadius: 14, fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase', fontSize: 15, border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: `0 8px 28px ${GS.greenGlow}` }}
            >
              Sign up free <ArrowRight style={{ width: 16, height: 16 }} />
            </button>
          </div>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────────── */}
      <footer className="safe-bottom max-w-6xl mx-auto px-5 py-10 flex flex-col sm:flex-row items-center justify-between gap-4" style={{ fontSize: 11, fontWeight: 800, color: GS.ink3, textTransform: 'uppercase', letterSpacing: '0.1em', borderTop: `1px solid ${GS.line}` }}>
        <span>© {new Date().getFullYear()} Dugout</span>
        <div className="flex gap-5">
          <button onClick={onShowTerms} style={{ background: 'none', border: 'none', cursor: 'pointer', color: GS.ink3, font: 'inherit' }}>Terms</button>
          <button onClick={onShowPrivacy} style={{ background: 'none', border: 'none', cursor: 'pointer', color: GS.ink3, font: 'inherit' }}>Privacy</button>
          {/* Absolute URL + _blank: on native, a relative href would navigate the
              WKWebView away from the SPA; this opens the system browser instead. */}
          <a href="https://www.dugoutbaseball.app/support.html" target="_blank" rel="noopener noreferrer" style={{ color: GS.ink3, font: 'inherit', textDecoration: 'none' }}>Support</a>
          <a href="https://apps.apple.com/tt/app/dugout-baseball-coach/id6781730520" target="_blank" rel="noopener noreferrer" style={{ color: GS.ink3, font: 'inherit', textDecoration: 'none' }}>App Store</a>
        </div>
      </footer>

      {/* ── Signup overlay (new coaches · "Create Your Account") ──
          Opened by the hero "Sign up free" button. Google one-tap up top,
          name/email/password below, and a link to the Sign In modal for
          returning coaches. The Name field feeds signupName → updateProfile. */}
      {signupOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(20,26,33,0.45)', backdropFilter: 'blur(4px)' }}
          onClick={() => setSignupOpen(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.18 }}
            className="w-full max-w-sm space-y-5"
            style={{ background: GS.panel, border: `1px solid ${GS.line}`, borderRadius: 24, padding: 24, boxShadow: '0 30px 70px -20px rgba(20,26,33,0.4)' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <img src="/dugout-mark.png" alt="Dugout" style={{ width: 28, height: 28, borderRadius: 8, boxShadow: `0 4px 14px ${GS.greenGlow}`, objectFit: 'cover' }} />
                <h2 style={{ fontSize: 18, fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase', letterSpacing: '-0.01em' }}>
                  Create your account
                </h2>
              </div>
              <button onClick={() => setSignupOpen(false)} aria-label="Close" style={{ color: GS.ink3, background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, lineHeight: 1, padding: '0 4px' }}>×</button>
            </div>

            {inAppBrowser && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, textAlign: 'left', background: GS.goldSoft, border: `1px solid ${GS.gold}`, color: GS.goldInk, borderRadius: 14, padding: '10px 14px' }}>
                <Info style={{ width: 16, height: 16, marginTop: 2, flexShrink: 0 }} />
                <p style={{ fontSize: 12, lineHeight: 1.5 }}>
                  <span style={{ fontWeight: 700 }}>Trouble with Google sign-in?</span> You're in an in-app browser. Open in Safari/Chrome, or use email below.
                </p>
              </div>
            )}

            <button
              onClick={onGoogleLogin}
              style={{ width: '100%', padding: 12, background: GS.ink, color: '#fff', borderRadius: 12, fontWeight: 900, fontSize: 14, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}
            >
              <img src="https://www.google.com/favicon.ico" style={{ width: 16, height: 16 }} alt="" />
              Continue with Google
            </button>

            {appleButton}

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1, height: 1, background: GS.line }} />
              <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', color: GS.ink3 }}>or</span>
              <div style={{ flex: 1, height: 1, background: GS.line }} />
            </div>

            <form onSubmit={onEmailSubmit} className="space-y-2.5">
              <input
                type="text" placeholder="Your name (e.g. Coach Jordan)" value={signupName}
                onChange={e => { setSignupName(e.target.value); setAuthError(''); }}
                style={inputStyle}
              />
              <input
                type="email" placeholder="Email address" value={email}
                onChange={e => { setEmail(e.target.value); setAuthError(''); }} required
                style={inputStyle}
              />
              <input
                type="password" placeholder="Password" value={password}
                onChange={e => { setPassword(e.target.value); setAuthError(''); }} required minLength={6}
                style={inputStyle}
              />
              {authError && <p style={{ color: GS.red, fontSize: 12, fontWeight: 500 }}>{authError}</p>}
              <button type="submit" disabled={authLoading} style={{ width: '100%', padding: 12, background: GS.green, color: '#fff', borderRadius: 12, fontWeight: 900, fontSize: 14, border: 'none', cursor: 'pointer', opacity: authLoading ? 0.6 : 1 }}>
                {authLoading ? '...' : 'Create Free Account'}
              </button>
            </form>

            {legalLine}

            <p style={{ textAlign: 'center', fontSize: 12, color: GS.ink2 }}>
              Already have an account?{' '}
              <button
                onClick={() => { setSignupOpen(false); openSignIn(); }}
                style={{ color: GS.greenInk, fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer' }}
              >
                Sign in
              </button>
            </p>
          </motion.div>
        </div>
      )}

      {/* ── Sign In modal (returning coaches) ───────────────────── */}
      {signInOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(20,26,33,0.45)', backdropFilter: 'blur(4px)' }}
          onClick={() => setSignInOpen(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.18 }}
            className="w-full max-w-sm space-y-5"
            style={{ background: GS.panel, border: `1px solid ${GS.line}`, borderRadius: 24, padding: 24, boxShadow: '0 30px 70px -20px rgba(20,26,33,0.4)' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <img src="/dugout-mark.png" alt="Dugout" style={{ width: 28, height: 28, borderRadius: 8, boxShadow: `0 4px 14px ${GS.greenGlow}`, objectFit: 'cover' }} />
                <h2 style={{ fontSize: 18, fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase', letterSpacing: '-0.01em' }}>
                  {showReset ? 'Reset password' : 'Welcome back, Coach'}
                </h2>
              </div>
              <button onClick={() => setSignInOpen(false)} aria-label="Close" style={{ color: GS.ink3, background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, lineHeight: 1, padding: '0 4px' }}>×</button>
            </div>

            <form onSubmit={onEmailSubmit} className="space-y-2.5">
              <input
                type="email" placeholder="Email address" value={email}
                onChange={e => { setEmail(e.target.value); setAuthError(''); }} required
                style={inputStyle}
              />
              {!showReset && (
                <input
                  type="password" placeholder="Password" value={password}
                  onChange={e => { setPassword(e.target.value); setAuthError(''); }} required minLength={6}
                  style={inputStyle}
                />
              )}
              {authError && <p style={{ color: GS.red, fontSize: 12, fontWeight: 500 }}>{authError}</p>}
              {resetSent && <p style={{ color: GS.greenInk, fontSize: 12, fontWeight: 500 }}>Reset email sent. Check your inbox.</p>}
              {!showReset ? (
                <button type="submit" disabled={authLoading} style={{ width: '100%', padding: 12, background: GS.green, color: '#fff', borderRadius: 12, fontWeight: 900, fontSize: 14, border: 'none', cursor: 'pointer', opacity: authLoading ? 0.6 : 1 }}>
                  {authLoading ? '...' : 'Sign In'}
                </button>
              ) : (
                <button type="button" onClick={onPasswordReset} disabled={authLoading} style={{ width: '100%', padding: 12, background: GS.ink2, color: '#fff', borderRadius: 12, fontWeight: 900, fontSize: 14, border: 'none', cursor: 'pointer', opacity: authLoading ? 0.6 : 1 }}>
                  {authLoading ? '...' : 'Send Reset Email'}
                </button>
              )}
            </form>

            <button onClick={() => { setShowReset(!showReset); setAuthError(''); }} style={{ color: GS.ink2, fontSize: 12, background: 'none', border: 'none', cursor: 'pointer' }}>
              {showReset ? '← Back to sign in' : 'Forgot password?'}
            </button>

            {!showReset && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ flex: 1, height: 1, background: GS.line }} />
                  <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', color: GS.ink3 }}>or</span>
                  <div style={{ flex: 1, height: 1, background: GS.line }} />
                </div>
                <button
                  onClick={onGoogleLogin}
                  style={{ width: '100%', padding: 12, background: GS.ink, color: '#fff', borderRadius: 12, fontWeight: 900, fontSize: 14, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}
                >
                  <img src="https://www.google.com/favicon.ico" style={{ width: 16, height: 16 }} alt="" />
                  Sign in with Google
                </button>
                {appleButton}
                <p style={{ textAlign: 'center', fontSize: 12, color: GS.ink2 }}>
                  New here?{' '}
                  <button
                    onClick={() => { setSignInOpen(false); openCreateAccount(); }}
                    style={{ color: GS.greenInk, fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    Create an account
                  </button>
                </p>
                {legalLine}
              </>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};
